/**
 * @file The only state that advances the simulation.
 *
 * It ticks the world, then updates the presentation rigs (camera, shadow focus)
 * from the player's transform. Presentation is deliberately updated after the
 * simulation so the camera never lags a frame behind.
 *
 * Never regenerates the level here — only Loading builds dungeons.
 */

import { GameState } from './GameState.js';
import { GameStates } from './StateMachine.js';
import { Transform, PlayerControlled } from '../../components/index.js';
import { Action } from '../../input/InputManager.js';
import { Events } from '../EventBus.js';
import { prepareNextLevel } from '../../world/Level.js';

export class PlayingState extends GameState {
  /**
   * @param {import('../Context.js').GameContext} ctx Shared game context.
   * @param {import('./StateMachine.js').StateMachine} machine Owning machine.
   */
  constructor(ctx, machine) {
    super(ctx, machine);
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /**
     * Seconds of slow motion left. Hit stop is applied here rather than inside
     * the engine because it is a gameplay-feel rule, not a timing rule: the
     * menu and the pause screen must never be affected by it.
     * @type {number}
     * @private
     */
    this._hitStop = 0;
    /** @type {boolean} @private */
    this._exiting = false;
  }

  /** @returns {void} */
  enter() {
    this._hitStop = 0;
    this._exiting = false;
    this._subscriptions.push(
      this.ctx.events.on(Events.DIED, ({ entity }) => {
        if (entity === this.ctx.player) {
          this.machine.change(GameStates.GAME_OVER);
          return;
        }
        this._hitStop = this.ctx.config.feel.hitStopDuration;
      })
    );
  }

  /** @returns {void} */
  exit() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    // Release held inputs so the player does not resume mid-move or firing.
    this.ctx.input.releaseAll();
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const { input, world, cameraController, lighting, config, arena } = this.ctx;

    if (input.justPressed(Action.PAUSE)) {
      this.machine.change(GameStates.PAUSED);
      return;
    }
    if (input.wheel !== 0) cameraController.zoom(input.wheel);

    // Hit stop runs on real time so the freeze lasts a fixed wall-clock moment,
    // while the world it slows down receives the scaled delta.
    let scale = 1;
    if (this._hitStop > 0) {
      this._hitStop -= dt;
      scale = config.feel.hitStopScale;
    }

    world.update(dt * scale);

    const player = this.ctx.player;
    if (!player?.alive) return;

    const transform = player.get(Transform);
    const control = player.get(PlayerControlled);
    cameraController.update(transform.position, control ? control.aimPoint : null, dt);
    lighting.focusOn(transform.position);

    // A → B: boss-room exit loads the next floor (only via Loading).
    if (!this._exiting && arena?.isAtExit?.(transform.position.x, transform.position.z)) {
      this._exiting = true;
      this.ctx.events.emit(Events.LEVEL_COMPLETE, {
        levelIndex: this.ctx.levelIndex,
        seed: arena.seed,
        name: arena.name
      });
      prepareNextLevel(this.ctx);
      this.machine.change(GameStates.LOADING);
    }
  }
}
